<template>
  <div class="booking-container" v-loading.fullscreen.lock="display.isLoading">
    <h3 class="centre">Book a Table</h3>
    <el-form class="reservation-form" ref="reservation" :model="reservation" :rules="rules.reservation">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item prop="customer.firstName">
            <el-input v-model="reservation.customer.firstName" placeholder="First Name (Required)"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="customer.surname">
            <el-input v-model="reservation.customer.surname" placeholder="Last Name (Required)"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item prop="customer.phone">
            <el-input type="number" v-model="reservation.customer.phone" placeholder="Mobile (Required)"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item prop="customer.email">
            <el-input type="email" v-model="reservation.customer.email" placeholder="Email (Required)"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item prop="attributes.numberOfCustomers">
            <el-select class="full-width" v-model="reservation.attributes.numberOfCustomers"
              placeholder="Number of Customers (Required)" @change="onChangeNumberOfCustomers">
              <el-option v-for="num in numberOfCustomersSelection" :key="num"
                :label="num === 1 ? '1 Customer' : `${num} Customers`" :value="num"></el-option>
            </el-select>
            <p class="cny-2021-number-of-customer-note" v-if="display.is2021ChineseNewYear">
              Minimum 6 customers are required to make a reservation during 2021
              Chinese New Year, 11 - 15 Feb 2021.
            </p>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item prop="selectedReservedTime.date">
            <el-select class="full-width" v-model="reservation.selectedReservedTime.date"
              placeholder="Select Date (Required)" @change="onChangeReservedTimeSelectionDate">
              <el-option v-for="date in reservedTimeSelectionDate" :key="date" :label="date" :value="date"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="selectedReservedTime.time">
            <el-select class="full-width" v-model="reservation.selectedReservedTime.time"
              placeholder="Select Time (Required)">
              <el-option v-for="time in reservedTimeSelectionTime" :key="time.display" :label="time.display"
                :value="time.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item prop="specialOccasions" class="special-occasions">
            <h4>Special Occasions</h4>
            <el-checkbox-group v-model="reservation.attributes.specialOccasions">
              <div v-for="req in specialOccasionsSelection" :key="req">
                <el-checkbox :label="req">{{ req }}</el-checkbox>
              </div>
            </el-checkbox-group>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item prop="comments">
            <el-input type="textarea" v-model="reservation.comments" placeholder="Additional Requests"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item>
            <el-input disabled v-model="reservation.attributes.tableNumber"
              placeholder="Reserved Table Number"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="24" class="consent-content">
          <h4>Data Collection & Consent</h4>
          <p>We need your personal information (e.g., name, email) to process your booking and provide services.</p>
        </el-col>
        <el-col :span="24">
          <el-form-item prop="customer.isConsent" class="consents">
            <el-checkbox v-model="reservation.customer.isConsent"
              label="I agree to the collection and processing of my personal information (required)"></el-checkbox>
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <el-form-item prop="customer.isConsentMarketing" class="consents">
            <el-checkbox v-model="reservation.customer.isConsentMarketing"
              label="I agree to receive promotional emails and special offers (optional)">
            </el-checkbox>
          </el-form-item>
        </el-col>
      </el-row>
      <div class="centre">
        <el-button v-if="!isReceiveBookingOnly" class="bg-gold black" @click="onClickBack">Back</el-button>
        <el-button class="gold bg-black" @click="onClickSubmit">Submit</el-button>
      </div>
    </el-form>

    <!-- Reservation confirm dialog -->
    <el-dialog :visible.sync="display.dialogs.confirm.isVisible" width="80%">
      <div>
        <h3 class="centre">Confirm Your Booking</h3>
        <el-row :gutter="20">
          <el-col :span="12" class="right">
            <strong>Name</strong>
          </el-col>
          <el-col :span="12">
            {{ reservation.customer.firstName }}
            {{ reservation.customer.surname }}
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12" class="right">
            <strong>Mobile</strong>
          </el-col>
          <el-col :span="12">{{ reservation.customer.phone }}</el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12" class="right">
            <strong>Customers</strong>
          </el-col>
          <el-col :span="12">
            {{ reservation.attributes.numberOfCustomers }}
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12" class="right">
            <strong>Reserved Time</strong>
          </el-col>
          <el-col :span="12">{{ reservation.display.reservedTime }}</el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12" class="right">
            <strong>
              Special
              <br />Occasions
            </strong>
          </el-col>
          <el-col :span="12">{{
            reservation.attributes.specialOccasions.join(", ")
          }}</el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12" class="right">
            <strong>
              Additional
              <br />Requests
            </strong>
          </el-col>
          <el-col :span="12">{{ reservation.comments }}</el-col>
        </el-row>
      </div>
      <div slot="footer" class="centre">
        <el-button v-if="!isReceiveBookingOnly" class="bg-gold black"
          @click="display.dialogs.confirm.isVisible = false">Back</el-button>
        <el-button class="bg-black gold" @click="onClickConfirmSubmit">Confirm</el-button>
      </div>
    </el-dialog>

    <!-- Reservation successful dialog -->
    <el-dialog width="80%" title="Booking Confirmed" :visible.sync="display.dialogs.success.isVisible"
      :show-close="false" center :close-on-click-modal="false">
      <div>
        Your booking request has been received. Thank you for using King Dynasty
        Online Services.
        {{ isReceiveBookingOnly ?
          'You can close this page now.' :
          'Hit "Back" to go back to the previous page, or "Pre-order" to add dishes in advance.'
        }}
      </div>
      <div slot="footer">
        <el-button v-if="!isReceiveBookingOnly" class="bg-gold black" @click="onClickBack">Back</el-button>
        <el-button v-if="!isReceiveBookingOnly" class="bg-black gold" @click="onClickOrder">
          Pre-order
        </el-button>
        <el-button v-if="isReceiveBookingOnly" @click="onClickClose">Close</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Cookies from "js-cookie";
import { mapGetters } from "vuex";
import moment from "moment";
import momentBusiness from "moment-business";
import { create, getReservationRulesByDate } from "@/api/reservation";
import { getMe } from "@/api/customer";
import { validateMobilePrefix, validateEmail, CONFIG_KEYS } from "@/utils";
import logger from "@/utils/logger";
import {
  getNumberOfCustomerSelections,
  getReservationDates,
  getReservationTimeslots,
} from "@/utils/dine-in";
import featureFlag from '@/utils/feature';

const name = "Reservation";
const log = logger(name);

export default {
  data() {
    return {
      isReceiveBookingOnly: featureFlag.RECEIVE_BOOKING_ONLY,

      reservation: {
        attributes: {
          numberOfCustomers: 10,
          specialOccasions: [],
        },
        customer: {
          isConsent: false,
          isConsentMarketing: false,
        },
        selectedReservedTime: {
          date: null,
          time: null,
        },
        display: {},
      },

      reservedTimeSelectionDate: getReservationDates(21),
      reservedTimeSelectionTime: [],
      numberOfCustomersSelection: getNumberOfCustomerSelections(1),
      specialOccasionsSelection: [
        "Birthday",
        "Anniversary",
        "Is this your first visit?",
      ],

      rules: {
        reservation: {
          "customer.firstName": [
            {
              required: true,
              message: "Please type first name.",
              trigger: "blur",
            },
          ],
          "customer.surname": [
            {
              required: true,
              message: "Please type surname.",
              trigger: "blur",
            },
          ],
          "customer.phone": [
            {
              required: true,
              message: "Please type mobile number.",
              trigger: "blur",
            },
            {
              min: 10,
              max: 10,
              message:
                "Please type a valid Australian mobile number (10 digits).",
              trigger: "blur",
            },
            { validator: validateMobilePrefix, trigger: "blur" },
          ],
          "customer.email": [
            {
              required: true,
              message: "Please type email address.",
              trigger: "blur",
            },
            {
              validator: validateEmail,
              trigger: 'blur'
            }
          ],
          "customer.isConsent": [
            {
              required: true,
              message: 'Please agree to the consent.',
              trigger: 'change'
            },
            {
              validator: (_, value, cb) => {
                if (value !== true) {
                  return cb(new Error('Please agree to the consent.'));
                }
                cb();
              },
              trigger: 'change'
            }
          ],
          "attributes.numberOfCustomers": [
            {
              required: true,
              message: "Please type number of customers.",
              trigger: "blur",
            },
          ],
          "selectedReservedTime.date": [
            {
              required: true,
              message: "Please select a booking date.",
              trigger: "blur",
            },
          ],
          "selectedReservedTime.time": [
            {
              required: true,
              message: "Please select a booking time.",
              trigger: "blur",
            },
          ],
        },
      },

      display: {
        isLoading: false,
        isFormDisabled: false,
        dialogs: {
          confirm: { isVisible: false },
          success: { isVisible: false },
        },
        is2021ChineseNewYear: false,
      },
    };
  },
  computed: {
    ...mapGetters(["customerId", "token"]),
  },
  async created() {
    this.display.isLoading = true;
    if (this.token) {
      this.reservation.customer = await getMe();
    }
    this.display.isLoading = false;
  },
  methods: {
    onClickBack() {
      this.$router.back();
    },
    onClickOrder() {
      Cookies.set("kingdynasty_ec_home_options_selected_mode", "DINE_IN");
      this.$router.push("/?hideHomeOptions=true");
    },
    onClickClose() {
      window.close();
    },
    onChangeNumberOfCustomers(event) {
      this.reservation.attributes.numberOfCustomers = Number(event);
    },
    async onChangeReservedTimeSelectionDate(event) {
      this.reservation.selectedReservedTime.date = event;
      this.reservation.selectedReservedTime.time = null;
      this.display.isLoading = true;
      try {
        const dateStr = moment(event).format("YYYY-MM-DD");
        const reservationRules = await getReservationRulesByDate(dateStr);
        this.reservedTimeSelectionTime = getReservationTimeslots(
          moment(event),
          reservationRules,
        );
        this._initNumberOfCustomerSelections(reservationRules);
      } catch (err) {
        log.error("Error when refreshing upon changing reserved date", err);
      } finally {
        this.display.isLoading = false;
      }
    },
    onClickSubmit() {
      this._beforeSave();
      this.$refs["reservation"].validate(async (valid) => {
        if (!valid) {
          log.warn("Reservation form invalid");
          return false;
        }
        const reservedTimeMoment = moment(this.reservation.reservedTime);
        if (reservedTimeMoment.isBefore(moment())) {
          log.warn("Selected reserved time is before current time");
          this.$message({
            type: "error",
            message: "Booking time must be after the current time.",
          });
          return false;
        }

        const isWeekdayReservation = momentBusiness.isWeekDay(
          reservedTimeMoment,
        );
        const isLunchReservation =
          reservedTimeMoment.hour() >= 10 && reservedTimeMoment.hour() < 17;
        if (reservedTimeMoment.isSame(moment(), "day")) {
          /* Dinner session 15min limit */
          if (
            reservedTimeMoment.isAfter(
              moment()
                .hours(16)
                .minutes(59),
            ) &&
            moment()
              .hour(reservedTimeMoment.hour())
              .minute(reservedTimeMoment.minute())
              .add(-15, "minute")
              .isBefore(moment())
          ) {
            log.warn(
              "Selected reserved time is within 15min of today dinner session",
            );
            this.$message({
              type: "error",
              message:
                "Sorry, reservation for today's dinner session must be made at least 15 minutes in advance.",
            });
            return false;
          }

          /* Weekday lunch session 30min limit */
          if (
            isWeekdayReservation &&
            isLunchReservation &&
            moment()
              .hour(reservedTimeMoment.hour())
              .minute(reservedTimeMoment.minute())
              .add(-30, "minute")
              .isBefore(moment())
          ) {
            log.warn(
              "Selected reserved time is within 30min of today weekday lunch session",
            );
            this.$message({
              type: "error",
              message:
                "Sorry, reservation for today's lunch session must be made at least 30 minutes in advance.",
            });
            return false;
          }

          /* Weekend lunch session 1h limit */
          if (
            !isWeekdayReservation &&
            isLunchReservation &&
            moment()
              .hour(reservedTimeMoment.hour())
              .minute(reservedTimeMoment.minute())
              .add(-1, "hour")
              .isBefore(moment())
          ) {
            log.warn(
              "Selected reserved time is within 1h of today weekend lunch session",
            );
            this.$message({
              type: "error",
              message:
                "Sorry, reservation for today's lunch session must be made at least 1 hour in advance.",
            });
            return false;
          }
        }

        if (
          Number(this.reservation.attributes.numberOfCustomers) < 4 &&
          !isWeekdayReservation &&
          isLunchReservation
        ) {
          /* Weekend lunch session - require 4 customers and onwards */
          log.warn(
            `${this.reservation.attributes.numberOfCustomers} customers fewer than 4, cannot make reservation for weekend lunch session`,
          );
          this.$message({
            type: "error",
            message:
              "Sorry, at least 4 customers are required to make a reservation for weekend lunch session.",
          });
          return false;
        }

        this.display.dialogs.confirm.isVisible = true;
      });
    },
    async onClickConfirmSubmit() {
      this.display.isLoading = true;
      try {
        const reservationCreated = await create(this.reservation);
        localStorage.setItem(CONFIG_KEYS.reservationId, reservationCreated._id);
        this.display.dialogs.confirm.isVisible = false;
        this.display.dialogs.success.isVisible = true;
      } catch (err) {
        if (err.response) {
          this.$message({
            message: err.response.data.message,
            type: "error",
          });
        } else {
          log.error("Error when creating reservation", err);
          this.$message({
            message: "System error",
            type: "error",
          });
        }
      } finally {
        this.display.isLoading = false;
      }
    },

    _beforeSave() {
      const selectedTimeMoment = moment(
        this.reservation.selectedReservedTime.time,
      );
      this.reservation.reservedTime = moment(
        this.reservation.selectedReservedTime.date,
      )
        .hours(selectedTimeMoment.hour())
        .minutes(selectedTimeMoment.minute())
        .seconds(selectedTimeMoment.second())
        .milliseconds(selectedTimeMoment.millisecond())
        .toDate();
      this.reservation.display.reservedTime = moment(
        this.reservation.reservedTime,
      ).format("hh:mma D MMM YYYY");
    },

    _initNumberOfCustomerSelections(reservationRules) {
      let min = 1;
      let max = 30;
      if (reservationRules) {
        min = reservationRules.numberOfCustomers.min;
        max = reservationRules.numberOfCustomers.max;
      }
      this.numberOfCustomersSelection = getNumberOfCustomerSelections(min, max);
      if (
        this.reservation.attributes.numberOfCustomers < min ||
        this.reservation.attributes.numberOfCustomers > max
      ) {
        this.reservation.attributes.numberOfCustomers = null;
      }
    },
  },
};
</script>

<style scoped>
.booking-container {
  padding: 1rem;
}

.reservation-form>>>input,
.reservation-form>>>textarea {
  border: 1px solid #c5b899;
  border-radius: 0;
}

.reservation-form h4 {
  margin: 0;
}

.reservation-form .special-occasions>>>.is-checked .el-checkbox__inner,
.reservation-form .consents>>>.is-checked .el-checkbox__inner {
  border: 1px solid #c5b899 !important;
  background-color: #c5b899 !important;
}

.reservation-form .special-occasions>>>.is-checked .el-checkbox__label,
.reservation-form .consents>>>.is-checked .el-checkbox__label {
  color: #c5b899 !important;
}

.reservation-form .consents>>>.el-checkbox {
  display: flex;
}

.reservation-form .consents>>>.el-checkbox__label {
  white-space: normal;
}

.reservation-form .consent-content p {
  font-size: 0.875rem;
}

.cny-2021-number-of-customer-note {
  font-size: 0.8rem;
  margin-bottom: 0;
  line-height: 1rem;
}
</style>
